import React, { useState } from "react";
import { doc, collection, setDoc, CollectionReference, deleteDoc } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { v4 as uuidv4 } from "uuid";
import { EstimateType, getRequiredScopesForEstimateType } from "../CreatorTools/MappingTool/utils/EstimateType";
import { IDatabaseLineItem } from "../CreatorTools/LineItemDBCreationToolPage";
import LineItemPermutationPreviewPanel from "./LineItemPermutationPreviewPanel";

export interface GeneralRoomLineItem {
	id?: string; // UUID for Firebase document indexing
	lineItemId: string;
	lineItemDefaultText: string;
	always?: boolean;
	ifExists?: boolean;
	sumDuplicateQuantities?: boolean;
	order?: number;
	selectedPermutation?: string; // The string representation of the selected permutation
	defaultProperties?: {
		isLikely?: boolean;
		measurement?: string;
		quantity?: number;
		comment?: string;
	}
}

export interface GeneralRoomCardProps {
	estimateType: EstimateType;
	organizationId: string;
}

interface LineItemModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (item: GeneralRoomLineItem) => void;
	editingItem?: GeneralRoomLineItem | null;
	availableLineItems: IDatabaseLineItem[];
	estimateType: EstimateType;
}

const LineItemModal: React.FC<LineItemModalProps> = ({ isOpen, onClose, onSave, editingItem, availableLineItems, estimateType }) => {
	const [searchValue, setSearchValue] = useState("");
	const [selectedLineItem, setSelectedLineItem] = useState<IDatabaseLineItem | null>(null);
	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const [selectedPermutation, setSelectedPermutation] = useState<string | undefined>(undefined);
	const [formData, setFormData] = useState<GeneralRoomLineItem>({
		lineItemId: "",
		lineItemDefaultText: "",
		always: false,
		ifExists: false,
		sumDuplicateQuantities: false,
		order: 1,
		selectedPermutation: undefined,
		defaultProperties: {
			isLikely: false,
			measurement: "",
			quantity: 1,
			comment: ""
		}
	});

	const requiredScopes = getRequiredScopesForEstimateType(estimateType).map(scope => scope as string);

	// Filter available line items based on search and estimate type scopes
	const filteredLineItems = availableLineItems?.filter(item => {
		// Basic filters
		if (item.deleted || !item.originalLineItem) return false;
		
		// Search filter
		if (searchValue && !item.originalLineItem.toLowerCase().includes(searchValue.toLowerCase())) return false;
		
		// Scope filter based on estimate type
		if (item.scopes && item.scopes.length > 0) {
			return item.scopes.some(scope => requiredScopes.includes(scope));
		}
		
		// If no scopes defined, exclude the item
		return false;
	}) || [];

	React.useEffect(() => {
		if (editingItem) {
			setFormData(editingItem);
			setSelectedPermutation(editingItem.selectedPermutation);
			// Find and set the selected line item for editing
			const lineItem = availableLineItems.find(item => item.id === editingItem.lineItemId);
			setSelectedLineItem(lineItem || null);
			setSearchValue("");
		} else {
			setFormData({
				lineItemId: "",
				lineItemDefaultText: "",
				always: false,
				ifExists: false,
				sumDuplicateQuantities: false,
				order: 1,
				selectedPermutation: undefined,
				defaultProperties: {
					isLikely: false,
					measurement: "",
					quantity: 1,
					comment: ""
				}
			});
			setSelectedLineItem(null);
			setSelectedPermutation(undefined);
			setSearchValue("");
		}
		setIsSearchFocused(false);
	}, [editingItem, isOpen, availableLineItems]);

	// Build default permutation string from line item elements
	const buildDefaultPermutationString = (item: IDatabaseLineItem): string => {
		if (!item.elements) return "";
		return item.elements.map(element => {
			if (element.type === "text") {
				return element.value;
			} else if (element.type === "variable") {
				return element.defaultValue ?? "";
			}
			return "";
		}).join(" ").replace(/\s+/g, " ").trim();
	};

	const handleSelectLineItem = (item: IDatabaseLineItem) => {
		setSelectedLineItem(item);
		const defaultPermutation = buildDefaultPermutationString(item);
		setSelectedPermutation(defaultPermutation);
		setFormData(prev => ({
			...prev,
			lineItemDefaultText: item.originalLineItem,
			lineItemId: item.id,
			selectedPermutation: defaultPermutation
		}));
		setIsSearchFocused(false);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (editingItem) {
			// Editing existing item - check if user selected a different line item
			const finalData = selectedLineItem 
				? {
					...formData,
					lineItemId: selectedLineItem.id,
					lineItemDefaultText: selectedLineItem.originalLineItem,
					selectedPermutation: selectedPermutation
				}
				: {
					...formData,
					selectedPermutation: selectedPermutation
				};
			
			if (finalData.lineItemDefaultText.trim()) {
				onSave(finalData);
				onClose();
			}
		} else {
			// Adding new item - require selection from search
			if (selectedLineItem && formData.lineItemDefaultText.trim()) {
				onSave({
					...formData,
					lineItemDefaultText: formData.lineItemDefaultText.trim(),
					selectedPermutation: selectedPermutation
				});
				onClose();
			}
		}
	};

	const updateDefaultProperty = (key: keyof NonNullable<GeneralRoomLineItem['defaultProperties']>, value: any) => {
		setFormData(prev => ({
			...prev,
			defaultProperties: {
				...prev.defaultProperties,
				[key]: value
			}
		}));
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
				<form onSubmit={handleSubmit}>
					<div className="flex justify-between items-center mb-6">
						<h3 className="text-xl font-semibold">
							{editingItem ? 'Edit Line Item' : 'Search and Add Line Item'}
						</h3>
						<button
							type="button"
							onClick={onClose}
							className="text-gray-500 hover:text-gray-700"
						>
							<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					<div className="grid grid-cols-2 gap-6">
						{/* Search Panel - Show for both add and edit modes */}
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									{editingItem ? 'Change Line Item' : `Search ${estimateType} Line Items`}
								</label>
								<div className="relative">
									<input
										type="text"
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										onFocus={() => setIsSearchFocused(true)}
										onBlur={(e) => {
											// Delay blur to allow clicking on search results
											setTimeout(() => {
												if (!(e.currentTarget?.contains(document.activeElement))) {
													setIsSearchFocused(false);
												}
											}, 150);
										}}
										className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										placeholder={editingItem 
											? `Search to change line item...` 
											: `Search ${estimateType.toLowerCase()} line items...`}
									/>
									<svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
								</div>
							</div>

							{isSearchFocused && (
								<div className="border rounded-lg max-h-96 overflow-y-auto">
									{filteredLineItems.length === 0 ? (
										<div className="p-4 text-center text-gray-500">
											{searchValue 
												? `No ${estimateType.toLowerCase()} line items found matching your search` 
												: editingItem
													? `Start typing to search for a different line item`
													: `Start typing to search ${estimateType.toLowerCase()} line items`}
										</div>
									) : (
										filteredLineItems.map((item) => (
											<div
												key={item.id}
												onClick={() => handleSelectLineItem(item)}
												className={`p-3 border-b cursor-pointer hover:bg-blue-50 ${
													selectedLineItem?.id === item.id || (editingItem && editingItem.lineItemId === item.id)
														? 'bg-blue-100 border-blue-300' : 'border-gray-200'
												}`}
											>
												<div className="flex items-center justify-between">
													<div>
														<div className="font-medium text-gray-900">
															{item.originalLineItem}
														</div>
														{item.category && (
															<div className="text-xs text-gray-500 mt-1">
																Category: {item.category}
															</div>
														)}
													</div>
													{editingItem && editingItem.lineItemId === item.id && (
														<div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
															Current
														</div>
													)}
												</div>
											</div>
										))
									)}
								</div>
							)}
						</div>

						{/* Configuration Panel */}
						<div className="space-y-4">
							{(selectedLineItem || editingItem) && (
								<>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											{editingItem ? 'Current Line Item' : 'Selected Line Item'}
										</label>
										<div className="p-3 bg-gray-50 border rounded-lg">
											<div className="font-medium">
												{editingItem ? formData.lineItemDefaultText : selectedLineItem?.originalLineItem}
											</div>
											{((editingItem && availableLineItems.find(item => item.id === editingItem.lineItemId)?.category) || selectedLineItem?.category) && (
												<div className="text-sm text-gray-600 mt-1">
													Category: {editingItem 
														? availableLineItems.find(item => item.id === editingItem.lineItemId)?.category
														: selectedLineItem?.category}
												</div>
											)}
										</div>
									</div>

									{/* Permutation Selection Panel */}
									{selectedLineItem && (
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Select Permutation
											</label>
											<LineItemPermutationPreviewPanel
												elements={selectedLineItem.elements}
												selectedPermutation={selectedPermutation}
												onPermutationChange={setSelectedPermutation}
											/>
										</div>
									)}

									<div className="grid grid-cols-2 gap-4">
										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												checked={formData.always || false}
												onChange={(e) => setFormData(prev => ({
													...prev,
													always: e.target.checked,
													ifExists: e.target.checked ? false : prev.ifExists,
													sumDuplicateQuantities: e.target.checked ? false : prev.sumDuplicateQuantities
												}))}
												className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
											/>
											<span className="text-sm font-medium text-gray-700">Always add to General Room</span>
										</label>

										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												checked={formData.ifExists || false}
												onChange={(e) => setFormData(prev => ({
													...prev,
													ifExists: e.target.checked,
													always: e.target.checked ? false : prev.always,
													sumDuplicateQuantities: e.target.checked ? prev.sumDuplicateQuantities : false
												}))}
												className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
											/>
											<span className="text-sm font-medium text-gray-700">Only if exists in another room</span>
										</label>
									</div>

									{formData.ifExists && (
										<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
											<label className="flex items-center space-x-2">
												<input
													type="checkbox"
													checked={formData.sumDuplicateQuantities || false}
													onChange={(e) => setFormData(prev => ({
														...prev,
														sumDuplicateQuantities: e.target.checked
													}))}
													className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
												/>
												<span className="text-sm font-medium text-gray-700">Do you want to sum the quantities of every occurrence of the line item in other rooms?</span>
												<div className="relative group">
													<svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
													<div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-64">
														<div className="text-left">
															By default, if there are multiple instances of this line item across rooms, we will pick the highest confidence one and add it to the general room.
														</div>
														<div className="text-left mt-2">
															If this box is checked, we'll sum the quantities of each of the line items and add that one to the general room.
														</div>
														<div className="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-gray-800"></div>
													</div>
												</div>
											</label>
										</div>
									)}

									{formData.always && (
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Order
											</label>
											<input
												type="number"
												step="1"
												value={formData.order || 1}
												onChange={(e) => setFormData(prev => ({ 
													...prev, 
													order: parseInt(e.target.value) || 1 
												}))}
												className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												min="1"
												placeholder="1"
											/>
											<div className="text-xs text-gray-500 mt-1">
												Order in which this line item appears in the room
											</div>
										</div>
									)}

									{formData.always && (
										<div className="border-t pt-4">
											<h4 className="text-lg font-medium text-gray-800 mb-4">Default Properties</h4>
											
											<div className="space-y-4">
												<label className="flex items-center space-x-2">
													<input
														type="checkbox"
														checked={formData.defaultProperties?.isLikely || false}
														onChange={(e) => updateDefaultProperty('isLikely', e.target.checked)}
														className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
													/>
													<span className="text-sm font-medium text-gray-700">Recommendation</span>
												</label>

												<div className="grid grid-cols-2 gap-4">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Unit
														</label>
														<input
															type="text"
															value={formData.defaultProperties?.measurement || ""}
															onChange={(e) => updateDefaultProperty('measurement', e.target.value)}
															className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
															placeholder="e.g., sf, ea"
														/>
													</div>

													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Quantity
														</label>
														<input
															type="number"
															step="0.01"
															value={formData.defaultProperties?.quantity || 1}
															onChange={(e) => updateDefaultProperty('quantity', parseFloat(e.target.value) || 1)}
															className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
															min="0.01"
															placeholder="e.g., 1.5"
														/>
													</div>
												</div>

												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">
														Comment (F9 Note)
													</label>
													<textarea
														value={formData.defaultProperties?.comment || ""}
														onChange={(e) => updateDefaultProperty('comment', e.target.value)}
														className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
														rows={3}
														placeholder="Enter F9 note to apply to the line item"
													/>
												</div>
											</div>
										</div>
									)}
								</>
							)}
						</div>
					</div>

					<div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={!editingItem && !selectedLineItem}
							className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
						>
							{editingItem ? 'Update' : 'Add'} Line Item
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

const GeneralRoomCard: React.FC<GeneralRoomCardProps> = ({ estimateType, organizationId }) => {
	
	// Get the Line Items for the selected customer and estimate type
	const firestoreCollectionPath = `general_room_properties/${organizationId}/${estimateType.toLowerCase()}`;
	const firestoreCollectionRef = collection(useFirestore(), firestoreCollectionPath) as CollectionReference<GeneralRoomLineItem>;
	const firebaseLineItems = useFirestoreCollectionData<GeneralRoomLineItem>(firestoreCollectionRef, { idField: "id" });
	
	// Get available line items from the database for searching
	const availableLineItemsPath = `tools`;
	const availableLineItemsRef = collection(useFirestore(), availableLineItemsPath) as CollectionReference<IDatabaseLineItem>;
	const availableLineItems = useFirestoreCollectionData<IDatabaseLineItem>(availableLineItemsRef, { idField: "id" });
	
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<GeneralRoomLineItem | null>(null);

	// Get line items from Firebase or use default data, sorted with "always" items first, then "ifExists" items
	const lineItems = (firebaseLineItems.data || []).sort((a, b) => {
		// First sort by type: "always" items come before "ifExists" items
		if (a.always && !b.always) return -1;
		if (!a.always && b.always) return 1;
		if (a.ifExists && !b.ifExists) return 1;
		if (!a.ifExists && b.ifExists) return -1;
		
		// Within the same type, sort by order
		return (a.order || 999) - (b.order || 999);
	});

	const addLineItem = async (item: GeneralRoomLineItem) => {
		try {
			const id = uuidv4();
			const itemWithId = { ...item, id };
			const docRef = doc(firestoreCollectionRef, id);
			await setDoc(docRef, itemWithId, { merge: false });
		} catch (error) {
			console.error("Error adding line item:", error);
		}
	};

	const updateLineItem = async (updatedItem: GeneralRoomLineItem) => {
		try {
			if (!updatedItem.id) {
				console.error("Cannot update line item without id");
				return;
			}
			const docRef = doc(firestoreCollectionRef, updatedItem.id);
			await setDoc(docRef, updatedItem, { merge: false });
		} catch (error) {
			console.error("Error updating line item:", error);
		}
	};

	const deleteLineItem = async (id: string) => {
		try {
			const docRef = doc(firestoreCollectionRef, id);
			await deleteDoc(docRef);
		} catch (error) {
			console.error("Error deleting line item:", error);
		}
	};

	const openAddModal = () => {
		setEditingItem(null);
		setIsModalOpen(true);
	};

	const openEditModal = (item: GeneralRoomLineItem) => {
		setEditingItem(item);
		setIsModalOpen(true);
	};

	const handleModalSave = async (item: GeneralRoomLineItem) => {
		if (editingItem) {
			await updateLineItem(item);
		} else {
			await addLineItem(item);
		}
	};

	// Show loading state while Firebase data is loading
	if (firebaseLineItems.status === 'loading') {
		return (
			<div className="space-y-6 mb-8">
				<h2 className="text-2xl font-semibold text-gray-800 mb-4">{estimateType} General Room</h2>
				<div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 shadow-sm">
					<div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
							<span className="ml-3 text-gray-600">Loading line items...</span>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show error state if Firebase fails
	if (firebaseLineItems.status === 'error') {
		return (
			<div className="space-y-6 mb-8">
				<h2 className="text-2xl font-semibold text-gray-800 mb-4">{estimateType} General Room</h2>
				<div className="bg-gradient-to-br from-red-50 to-red-50 rounded-xl border border-red-200 p-6 shadow-sm">
					<div className="bg-white rounded-lg border border-red-200 p-4 shadow-sm">
						<div className="text-red-600 text-center py-8">
							<svg className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
							</svg>
							<p>Error loading line items from database</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="space-y-6 mb-8">
				<h2 className="text-2xl font-semibold text-gray-800 mb-4">{estimateType} General Room</h2>
				
				<div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 shadow-sm">
					<div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
						<div className="mb-4 flex justify-between items-center">
							<h3 className="text-lg font-medium text-gray-800">Line Items</h3>
							<button
								onClick={openAddModal}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
							>
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
								</svg>
								Add Line Item
							</button>
						</div>
						
						<div className="bg-gray-50 rounded-lg border border-gray-200">
							{lineItems.length === 0 ? (
								<div className="text-gray-500 text-center py-8">
									<svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v16m8-8H4" />
									</svg>
									<p className="text-sm">No line items added yet</p>
								</div>
							) : (
								<div className="p-4 space-y-2">
									{lineItems.map((item) => (
										<div
											key={item.id}
											className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
											onClick={() => openEditModal(item)}
										>
											<div className="flex-1">
												<div className="flex items-center gap-2 flex-wrap">
													{item.always && (
														<span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono min-w-[2rem] text-center">
															{item.order || 1}
														</span>
													)}
													<span className="text-gray-800 font-medium">{item.lineItemDefaultText}</span>
													{item.always && (
														<span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Always</span>
													)}
													{item.ifExists && (
														<span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">If Exists</span>
													)}
													{item.sumDuplicateQuantities && (
														<span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">Sum Quantities</span>
													)}
													{item.selectedPermutation && (
														<span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded flex items-center gap-1">
															<svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
															</svg>
															{item.selectedPermutation}
														</span>
													)}
												</div>
												{item.always && item.defaultProperties?.isLikely && (
													<div className="text-xs text-green-600 mt-1 ml-12">• Recommendation</div>
												)}
											</div>
											
											{item.always && item.defaultProperties && item.defaultProperties.quantity && item.defaultProperties.measurement && (
												<div className="text-sm text-gray-700 font-medium mr-24">
													{item.defaultProperties.quantity} {item.defaultProperties.measurement}
												</div>
											)}
											<button
												onClick={(e) => {
													e.stopPropagation();
													if (item.id) deleteLineItem(item.id);
												}}
												className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
												title="Delete line item"
											>
												<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
												</svg>
											</button>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<LineItemModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onSave={handleModalSave}
				editingItem={editingItem}
				availableLineItems={availableLineItems.data || []}
				estimateType={estimateType}
			/>
		</>
	);
};

export default GeneralRoomCard;